import { Component, OnInit, inject, ViewChild } from '@angular/core';
import { TranslationService } from '../../services/translation.service';
import { CommonModule, DOCUMENT } from '@angular/common';
// import { AccessPoint } from '../../interface/access-point-interface';
// import { SyntheticGridConfig } from '../../interface/synthetic-grid-config-interface';
import {
  AccessPoint,
  DEFAULT_SYNTHETIC_GRID_CONFIG,
  Obstacle,
  SyntheticGridConfig,
  SyntheticGridService
} from '../../services/commons/synthetic-grid.service';
import { AntennaService } from '../../services/api/antenna/antenna.service';
import { ObstacleService } from '../../services/api/obstacle/obstacle.service';
import { AntennaList, AntennaListDataArray } from '../../model/antenna/antenna-list';
import { HeatPoint } from '../../model/heat-point/heat-point';
import { Obstacle as ObstacleMaterial, ObstacleDataArray } from '../../model/obstacle/obstacle';
import { Paging } from '../../model/paging/paging';
import { FloorPlan, FloorPlanDataArray } from '../../model/floorplan/floorplan';
import { Project, ProjectDataArray } from '../../model/project/project';
import { FloorplanCoverageComponent } from '../../features/floorplan-coverage/floorplan-coverage.component';
import {
  CoverageCanvasAction,
  CoverageInteractionTool,
  CoverageSelection
} from '../../features/floorplan-coverage/coverage-interaction.models';
import { CoverageContextPanelComponent } from '../../features/floorplan-coverage/coverage-context-panel.component';
import {
  clampCellSize,
  clampObservationHeight,
  computeAnalysisSummary,
  computeScenarioState,
  createAccessPointAt,
  createInitialConfigText,
  createInitialWorkspaceConfig,
  createObstacleFromLine,
  HeatmapAnalysisSummary,
  normalizeWorkspaceConfig,
  randomizeWorkspaceConfig,
  WorkspaceMode
} from './heatmap-workspace.helpers';
import { ProjectService } from '../../services/api/project/project.service';
import { FloorPlanService } from '../../services/api/floorplan/floorplan.service';

type CoverageMode =
  | 'heatmap'
  | 'contours-marching';

type TopbarPanel = 'workflow' | 'scenario' | 'analysis';
@Component({
	selector: 'app-heatmap-page',
	standalone: true,
	templateUrl: './heatmap-page.component.html',
	styleUrls: ['./heatmap-page.component.scss'],
	imports: [CommonModule, FloorplanCoverageComponent, CoverageContextPanelComponent],
})
export class HeatmapPageComponent implements OnInit {
	@ViewChild('coverage') private coverageComp?: FloorplanCoverageComponent;
	protected readonly i18n = inject(TranslationService);
  private readonly documentRef = inject(DOCUMENT);
  protected readonly targetCoverageThreshold = 65;
  protected imageWidth = 1280;
  protected imageHeight = 720;
  protected floorplanUrl = new URL('assets/floorplans/sample-floorplan.png', this.documentRef.baseURI).toString();
  protected projects: Project[] = [];
  protected floorplans: FloorPlan[] = [];
  protected selectedProjectId = '';
  protected selectedFloorplanId = '';
  protected projectsLoading = false;
  protected floorplansLoading = false;
  protected points: HeatPoint[] = [];
  protected accessPoints: AccessPoint[] = [];
  protected obstacles: Obstacle[] = [];
  protected antennaCatalog: AntennaList[] = [];
  protected obstacleCatalog: ObstacleMaterial[] = [];
  protected selectedAntennaTemplateId = '';
  protected selectedObstacleTemplateId = '';
  protected observationHeight = DEFAULT_SYNTHETIC_GRID_CONFIG.observationHeight ?? 1;
  protected readonly thresholds = [20, 30, 40, 50, 55, 60, 65, 70, 75, 80, 85, 90];
  protected readonly palette = [
    '#d50000',
    '#ff8f00',
    '#ffeb3b',
    '#9ccc65',
    '#4caf50',
    '#00bfa5',
    '#009cdc',
    '#0074d9',
    '#6f42c1',
    '#5e2ca5',
    '#4c1c82',
    '#3b0d52'
  ];
  protected loading = false;
  protected error?: string;
  protected configError?: string;
  protected mode: CoverageMode = 'heatmap';
  protected workspaceMode: WorkspaceMode = 'edit';
  protected activeTool: CoverageInteractionTool = 'select';
  protected heatmapOpacity = 0.9;
  protected hoveredScaleIndex: number | null = null;
  protected contextSelection?: CoverageSelection;
  protected zoom = 1;
  protected readonly minZoom = 0.6;
  protected readonly maxZoom = 2;
  private readonly zoomStep = 0.1;
  protected panX = 0;
  protected panY = 0;
  protected isPanning = false;
  private panPointerId?: number;
  private panStart?: { x: number; y: number };
  protected showConfigModal = false;
  protected configText = createInitialConfigText();
  protected readonly defaultConfig = DEFAULT_SYNTHETIC_GRID_CONFIG;
  protected cellSize = DEFAULT_SYNTHETIC_GRID_CONFIG.cell;
  protected randomizePositions = false;
  protected needsSimulation = true;
  protected scenarioSaved = false;
  protected scenarioSavedAt?: string;
  protected measurementDistancePx?: number;
  protected activeTopbarPanel: TopbarPanel | null = null;
  private currentConfig: SyntheticGridConfig = createInitialWorkspaceConfig();
  private readonly projectPaging = new Paging();
  private readonly floorplanPaging = new Paging();
  private readonly antennaPaging = new Paging();
  private readonly obstaclePaging = new Paging();
  protected readonly modeOptions: { value: CoverageMode; label: string; }[] = [
    {
      value: 'heatmap',
      label: 'Heatmap – Intensa (heatmap.js)',
    },
    {
      value: 'contours-marching',
      label: 'Contours – Marching Squares',
    }
  ];
  constructor(
    private syntheticGridService: SyntheticGridService,
    private projectService: ProjectService,
    private floorPlanService: FloorPlanService,
    private antennaService: AntennaService,
    private obstacleService: ObstacleService
  ) {}

  ngOnInit(): void {
    this.antennaPaging.pageSize = 200;
    this.obstaclePaging.pageSize = 200;
    this.loadProjects();
    this.loadAntennaCatalog();
    this.loadObstacleCatalog();
  }

  protected get workspaceProjectName(): string {
    return this.projects.find((project) => project.projectId === this.selectedProjectId)?.name ?? '—';
  }

  protected get workspaceFloorplanName(): string {
    return this.floorplans.find((floorplan) => floorplan.floorPlanId === this.selectedFloorplanId)?.name ?? '—';
  }

  protected setWorkspaceMode(mode: WorkspaceMode): void {
    this.workspaceMode = mode;
  }

  protected get selectedAntennaValue(): string {
    return this.selectedAntennaTemplateId;
  }

  protected get selectedObstacleValue(): string {
    return this.selectedObstacleTemplateId;
  }

  protected get hasContextPanel(): boolean {
    return !!this.contextSelection;
  }

  protected get selectedAntennaTemplate(): AntennaList | undefined {
    return this.antennaCatalog.find((item) => item.antennaId === this.selectedAntennaTemplateId);
  }

  protected get selectedObstacleTemplate(): ObstacleMaterial | undefined {
    return this.obstacleCatalog.find((item) => item.materialId === this.selectedObstacleTemplateId);
  }

  protected setInteractionTool(tool: CoverageInteractionTool): void {
    this.activeTool = tool;
    if (tool !== 'measure') {
      this.measurementDistancePx = undefined;
    }
  }

  protected toggleTopbarPanel(panel: TopbarPanel): void {
    this.activeTopbarPanel = this.activeTopbarPanel === panel ? null : panel;
  }

  protected resetView(): void {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
  }

  protected saveScenario(): void {
    this.scenarioSaved = true;
    this.scenarioSavedAt = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  }

  protected get measurementLabel(): string {
    if (this.measurementDistancePx === undefined) {
      return '—';
    }
    return `${this.measurementDistancePx.toFixed(1)} px`;
  }

  protected get scenarioStateLabel(): string {
    return this.i18n.translate(this.scenarioState.labelKey);
  }

  protected get scenarioStateTone(): 'neutral' | 'warning' | 'success' | 'active' {
    return this.scenarioState.tone;
  }

  protected get analysisReady(): boolean {
    return this.points.length > 0 && !this.needsSimulation && !this.loading;
  }

  private get scenarioState() {
    return computeScenarioState({
      loading: this.loading,
      accessPoints: this.accessPoints,
      obstacles: this.obstacles,
      needsSimulation: this.needsSimulation,
      scenarioSaved: this.scenarioSaved
    });
  }

  private get analysisSummary(): HeatmapAnalysisSummary {
    return computeAnalysisSummary(this.points, this.targetCoverageThreshold);
  }

  protected get averageSignal(): number | null {
    return this.analysisSummary.averageSignal;
  }

  protected get bestSignal(): number | null {
    return this.analysisSummary.bestSignal;
  }

  protected get coveragePercentage(): number | null {
    return this.analysisSummary.coveragePercentage;
  }

  protected formatMetric(value: number | null, suffix = ''): string {
    if (value === null || Number.isNaN(value)) {
      return '—';
    }
    return `${value}${suffix}`;
  }

  protected setMode(mode: CoverageMode): void {
    this.mode = mode;
  }

  protected onModeSelect(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as CoverageMode;
    this.setMode(value);
  }

  protected onProjectSelect(event: Event): void {
    const projectId = (event.target as HTMLSelectElement).value;
    this.selectedProjectId = projectId;
    this.selectedFloorplanId = '';
    this.floorplans = [];
    this.resetWorkspaceScene();
    if (projectId) {
      this.loadFloorplans(projectId);
    }
  }

  protected onFloorplanSelect(event: Event): void {
    const floorplanId = (event.target as HTMLSelectElement).value;
    this.selectedFloorplanId = floorplanId;
    this.resetWorkspaceScene();
    if (floorplanId) {
      this.loadFloorplanDetail(floorplanId);
    }
  }

  protected onHeatmapOpacityChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    if (Number.isNaN(value)) {
      return;
    }
    this.heatmapOpacity = Math.min(1, Math.max(0.05, value));
  }

  protected zoomIn(): void {
    this.zoom = Math.min(this.maxZoom, Number((this.zoom + this.zoomStep).toFixed(2)));
  }

  protected zoomOut(): void {
    this.zoom = Math.max(this.minZoom, Number((this.zoom - this.zoomStep).toFixed(2)));
  }

  protected onPanStart(event: PointerEvent): void {
    const canPanWithPrimary = this.activeTool === 'pan' && event.button === 0;
    const canPanWithMiddle = event.button === 1;
    if ((!canPanWithPrimary && !canPanWithMiddle) || this.zoom <= 1) {
      return;
    }
    event.preventDefault();
    this.isPanning = true;
    this.panPointerId = event.pointerId;
    this.panStart = { x: event.clientX, y: event.clientY };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  protected onPanMove(event: PointerEvent): void {
    if (!this.isPanning || !this.panStart) {
      return;
    }
    event.preventDefault();
    const dx = event.clientX - this.panStart.x;
    const dy = event.clientY - this.panStart.y;
    this.panX += dx;
    this.panY += dy;
    this.panStart = { x: event.clientX, y: event.clientY };
  }

  protected onPanEnd(event?: PointerEvent): void {
    if (event && typeof this.panPointerId === 'number') {
      (event.currentTarget as HTMLElement).releasePointerCapture?.(this.panPointerId);
    }
    this.isPanning = false;
    this.panPointerId = undefined;
    this.panStart = undefined;
  }

  protected onSignalHover(value?: number): void {
    if (value === undefined || Number.isNaN(value) || this.thresholds.length === 0) {
      this.hoveredScaleIndex = null;
      return;
    }
    let index = 0;
    for (let i = 0; i < this.thresholds.length; i += 1) {
      if (value >= this.thresholds[i]) {
        index = i;
      }
    }
    this.hoveredScaleIndex = index;
  }

  protected modeLabel(mode: CoverageMode): string {
    return this.modeOptions.find((option) => option.value === mode)?.label ?? mode;
  }

  // protected modeHint(mode: CoverageMode): string {
  //   return '';
  // }

  protected openConfigModal(): void {
    this.showConfigModal = true;
    this.configError = undefined;
    this.configText = JSON.stringify(this.currentConfig, null, 2);
    this.cellSize = this.currentConfig.cell;
    this.observationHeight = this.currentConfig.observationHeight ?? 1;
  }

  protected closeConfigModal(): void {
    this.showConfigModal = false;
    this.configError = undefined;
  }

  protected onConfigTextChange(event: Event): void {
    this.configText = (event.target as HTMLTextAreaElement).value;
  }

  protected onCellSizeChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.cellSize = clampCellSize(value);
  }

  protected onRandomizeToggle(event: Event): void {
    this.randomizePositions = (event.target as HTMLInputElement).checked;
  }

  protected onObservationHeightChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.observationHeight = clampObservationHeight(value);
  }

  protected applyConfigFromModal(): void {
    try {
      const parsed = JSON.parse(this.configText) as SyntheticGridConfig;
      if (!this.isValidConfig(parsed)) {
        this.configError = 'Config incompleta: assicurati che width, height, cell, aps e obstacles siano presenti.';
        return;
      }
      parsed.cell = clampCellSize(this.cellSize);
      parsed.observationHeight = clampObservationHeight(parsed.observationHeight ?? this.observationHeight);
      const nextConfig = this.randomizePositions ? randomizeWorkspaceConfig(parsed, this.observationHeight) : parsed;
      this.loadGridFromConfig(nextConfig, true);
    } catch (err) {
      this.configError = 'JSON non valido: controlla parentesi, virgole e campi obbligatori.';
    }
  }

  protected resetConfigText(): void {
    this.configText = JSON.stringify(this.defaultConfig, null, 2);
    this.configError = undefined;
    this.cellSize = this.defaultConfig.cell;
    this.observationHeight = this.defaultConfig.observationHeight ?? 1;
  }

  protected randomizeConfigText(): void {
    try {
      const parsed = JSON.parse(this.configText) as SyntheticGridConfig;
      if (!this.isValidConfig(parsed)) {
        this.configError = 'Config incompleta: assicurati che width, height, cell, aps e obstacles siano presenti.';
        return;
      }
      const randomized = randomizeWorkspaceConfig(parsed, this.observationHeight);
      randomized.cell = clampCellSize(this.cellSize);
      randomized.observationHeight = clampObservationHeight(randomized.observationHeight ?? this.observationHeight);
      this.configText = JSON.stringify(randomized, null, 2);
      this.configError = undefined;
    } catch {
      this.configError = 'JSON non valido: controlla parentesi, virgole e campi obbligatori.';
    }
  }

  protected addAccessPoint(): void {
    this.setInteractionTool('place-ap');
  }

  protected addAccessPointAt(x: number, y: number): void {
    const nextAp: AccessPoint = createAccessPointAt(x, y, this.imageWidth, this.selectedAntennaTemplate);
    this.applyScenarioDraft({ aps: [...this.accessPoints, nextAp] });
  }

  protected addObstacle(): void {
    this.setInteractionTool('draw-obstacle');
  }

  protected onAntennaSelectChange(event: Event): void {
    this.selectedAntennaTemplateId = (event.target as HTMLSelectElement).value;
  }

  protected onObstacleSelectChange(event: Event): void {
    this.selectedObstacleTemplateId = (event.target as HTMLSelectElement).value;
  }

  protected addObstacleFromLine(startX: number, startY: number, endX: number, endY: number): void {
    const nextObstacle: Obstacle = createObstacleFromLine(startX, startY, endX, endY, this.selectedObstacleTemplate);
    this.applyScenarioDraft({ obstacles: [...this.obstacles, nextObstacle] });
  }

  protected onCanvasAction(event: CoverageCanvasAction): void {
    if (event.kind === 'place-ap') {
      this.addAccessPointAt(event.x, event.y);
      return;
    }
    this.addObstacleFromLine(event.startX, event.startY, event.endX, event.endY);
  }

  protected onMeasurementChange(value?: number): void {
    this.measurementDistancePx = value;
  }

  protected simulateCoverage(): void {
    this.error = undefined;
    if (!this.accessPoints.length) {
      this.points = [];
      this.needsSimulation = true;
      this.error = 'Aggiungi almeno un\'antenna e premi "Simula".';
      return;
    }
    const configToRun: SyntheticGridConfig = {
      ...this.currentConfig,
      aps: [...this.accessPoints],
      obstacles: [...this.obstacles],
      observationHeight: this.observationHeight
    };
    this.loadGridFromConfig(configToRun);
  }

  private loadGridFromConfig(config: SyntheticGridConfig, closeModal = false): void {
    if (!this.isValidConfig(config)) {
      this.configError = 'Config incompleta: assicurati che width, height, cell, aps e obstacles siano presenti.';
      return;
    }
    const normalizedConfig = normalizeWorkspaceConfig(config, this.observationHeight);
    if (!normalizedConfig.aps.length) {
      this.loading = false;
      this.points = [];
      this.applyScenarioSnapshot(normalizedConfig);
      this.needsSimulation = true;
      this.error = closeModal ? undefined : this.error;
      if (closeModal) {
        this.closeConfigModal();
      }
      return;
    }
    this.loading = true;
    this.error = undefined;
    this.syntheticGridService.fetchSyntheticGrid(normalizedConfig).subscribe({
      next: (points) => {
        this.points = points;
        this.loading = false;
        this.applyScenarioSnapshot(normalizedConfig);
        this.needsSimulation = false;
        this.workspaceMode = 'analyze';
        if (closeModal) {
          this.closeConfigModal();
        }
      },
      error: () => {
        this.error = 'Unable to load sample dataset.';
        this.loading = false;
        this.needsSimulation = true;
      }
    });
  }

  private isValidConfig(config: SyntheticGridConfig | undefined): config is SyntheticGridConfig {
    return !!config &&
      typeof config.width === 'number' &&
      typeof config.height === 'number' &&
      typeof config.cell === 'number' &&
      Array.isArray(config.aps) &&
      Array.isArray((config as any).obstacles ?? []);
  }

  protected onAccessPointDropped(event: { index: number; x: number; y: number; x2: number; y2: number; height?: number }): void {
    const nextAps = this.accessPoints.map((ap, idx) =>
      idx === event.index
        ? { ...ap, x: event.x, y: event.y, x2: event.x2, y2: event.y2, height: event.height ?? ap.height ?? 7 }
        : ap
    );
    this.applyScenarioDraft({ aps: nextAps });
  }

  protected onContextMenuChange(selection?: CoverageSelection): void {
    this.contextSelection = selection;
    if (selection) {
      this.activeTool = 'select';
    }
  }

  protected onPanelClose(): void {
    this.contextSelection = undefined;
  }

  protected onPanelAntennaHeightChange(event: { index: number; height: number }): void {
    const ap = this.accessPoints[event.index];
    if (!ap) {
      return;
    }
    this.onAccessPointDropped({
      index: event.index,
      x: ap.x,
      y: ap.y,
      x2: ap.x2 ?? ap.x,
      y2: ap.y2 ?? ap.y,
      height: event.height
    });
  }

  protected onPanelObstacleMaterialChange(event: { index: number; material: string; loss: number }): void {
    this.updateObstacleFromPanel(event.index, { material: event.material, loss: event.loss });
  }

  protected onPanelObstacleLossChange(event: { index: number; loss: number }): void {
    this.updateObstacleFromPanel(event.index, { loss: event.loss });
  }

  protected onPanelObstacleThicknessChange(event: { index: number; thickness: number }): void {
    this.updateObstacleFromPanel(event.index, { thickness: event.thickness });
  }

  protected onPanelObstacleHeightChange(event: { index: number; height: number }): void {
    this.updateObstacleFromPanel(event.index, { height: event.height });
  }

  protected onPanelDeleteAntenna(index: number): void {
    this.onDeleteAntenna(index);
    if (this.contextSelection?.kind === 'antenna' && this.contextSelection.index === index) {
      this.contextSelection = undefined;
    }
  }

  protected onPanelDeleteObstacle(index: number): void {
    this.onDeleteObstacle(index);
    if (this.contextSelection?.kind === 'obstacle' && this.contextSelection.index === index) {
      this.contextSelection = undefined;
    }
  }

  protected onObstacleChanged(event: {
    index: number;
    x: number;
    y: number;
    x2: number;
    y2: number;
    material: string;
    loss: number;
    thickness?: number;
    height?: number;
  }): void {
    const updates: Obstacle[] = [];
    for (let i = 0; i < this.obstacles.length; i++) {
      if (i === event.index) {
        updates.push({
          ...this.obstacles[i],
          x: event.x,
          y: event.y,
          x2: event.x2,
          y2: event.y2,
          material: event.material,
          loss: event.loss,
          thickness: event.thickness ?? this.obstacles[i].thickness,
          height: event.height ?? this.obstacles[i].height
        });
      } else {
        updates.push(this.obstacles[i]);
      }
    }
    const snapped = this.snapObstacleEndpoints(updates);
    this.applyScenarioDraft({ obstacles: snapped });
  }

  protected onDeleteAntenna(index: number): void {
    this.applyScenarioDraft({ aps: this.accessPoints.filter((_, i) => i !== index) });
  }

  protected onDeleteObstacle(index: number): void {
    this.applyScenarioDraft({ obstacles: this.obstacles.filter((_, i) => i !== index) });
  }

  private updateObstacleFromPanel(index: number, updates: Partial<Obstacle>): void {
    const obstacle = this.obstacles[index];
    if (!obstacle) {
      return;
    }
    this.onObstacleChanged({
      index,
      x: obstacle.x,
      y: obstacle.y,
      x2: obstacle.x2,
      y2: obstacle.y2,
      material: updates.material ?? obstacle.material,
      loss: updates.loss ?? obstacle.loss,
      thickness: updates.thickness ?? obstacle.thickness,
      height: updates.height ?? obstacle.height
    });
  }

  protected exportPdf(): void {
    const timestamp = new Date().toLocaleString();
    this.coverageComp?.exportAsPdf({
      title: 'Floorplan Coverage',
      subtitle: `Modalità: ${this.modeLabel(this.mode)} — H osservazione: ${this.observationHeight} m`,
      timestamp,
      footer: 'Generated by Coverage Tool'
    });
  }

  private loadProjects(): void {
    this.projectsLoading = true;
    this.projectService.GETPaginatedProjects(this.projectPaging).subscribe({
      next: (response: ProjectDataArray) => {
        this.projects = response?.data?.items ?? [];
        this.projectsLoading = false;
        if (this.projects.length && !this.selectedProjectId) {
          this.selectedProjectId = this.projects[0].projectId;
          this.loadFloorplans(this.selectedProjectId);
        }
      },
      error: () => {
        this.projectsLoading = false;
      }
    });
  }

  private loadAntennaCatalog(): void {
    this.antennaService.GETPaginatedAntennas(this.antennaPaging, { enabled: true }).subscribe({
      next: (response: AntennaListDataArray) => {
        this.antennaCatalog = response?.data?.items ?? [];
        if (!this.selectedAntennaTemplateId && this.antennaCatalog.length) {
          this.selectedAntennaTemplateId = this.antennaCatalog[0].antennaId;
        }
      },
      error: () => {
        this.antennaCatalog = [];
      }
    });
  }

  private loadObstacleCatalog(): void {
    this.obstacleService.GETPaginatedObstacles(this.obstaclePaging).subscribe({
      next: (response: ObstacleDataArray) => {
        this.obstacleCatalog = response?.data?.items ?? [];
        if (!this.selectedObstacleTemplateId && this.obstacleCatalog.length) {
          this.selectedObstacleTemplateId = this.obstacleCatalog[0].materialId;
        }
      },
      error: () => {
        this.obstacleCatalog = [];
      }
    });
  }

  private loadFloorplans(projectId: string): void {
    this.floorplansLoading = true;
    this.floorPlanService.GETPaginatedFloorPlans(projectId, this.floorplanPaging).subscribe({
      next: (response: FloorPlanDataArray) => {
        this.floorplans = response?.data?.items ?? [];
        this.floorplansLoading = false;
        if (this.floorplans.length) {
          this.selectedFloorplanId = this.floorplans[0].floorPlanId;
          this.loadFloorplanDetail(this.selectedFloorplanId);
        }
      },
      error: () => {
        this.floorplansLoading = false;
      }
    });
  }

  private loadFloorplanDetail(floorplanId: string): void {
    this.floorplansLoading = true;
    this.floorPlanService.GETFloorPlanById(floorplanId).subscribe({
      next: (response: any) => {
        const floorplan = response?.data as FloorPlan | undefined;
        if (floorplan) {
          this.applyFloorplanToWorkspace(floorplan);
        }
        this.floorplansLoading = false;
      },
      error: () => {
        this.floorplansLoading = false;
      }
    });
  }

  private applyFloorplanToWorkspace(floorplan: FloorPlan): void {
    const svgContent = floorplan.svgContent?.trim();
    if (svgContent) {
      this.floorplanUrl = this.svgToDataUrl(svgContent);
      const parsedSize = this.extractSvgSize(svgContent);
      this.imageWidth = parsedSize.width;
      this.imageHeight = parsedSize.height;
    } else {
      this.floorplanUrl = new URL('assets/floorplans/sample-floorplan.png', this.documentRef.baseURI).toString();
      this.imageWidth = 1280;
      this.imageHeight = 720;
    }
    this.resetView();
  }

  private svgToDataUrl(svgContent: string): string {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
  }

  private extractSvgSize(svgContent: string): { width: number; height: number } {
    const fallback = { width: 1280, height: 720 };
    try {
      const doc = new DOMParser().parseFromString(svgContent, 'image/svg+xml');
      const svg = doc.documentElement;
      const viewBox = svg.getAttribute('viewBox');
      if (viewBox) {
        const [, , width, height] = viewBox.split(/[\s,]+/).map(Number);
        if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
          return { width, height };
        }
      }
      const widthAttr = Number(svg.getAttribute('width'));
      const heightAttr = Number(svg.getAttribute('height'));
      if (Number.isFinite(widthAttr) && Number.isFinite(heightAttr) && widthAttr > 0 && heightAttr > 0) {
        return { width: widthAttr, height: heightAttr };
      }
      return fallback;
    } catch {
      return fallback;
    }
  }

  private resetWorkspaceScene(): void {
    this.points = [];
    this.accessPoints = [];
    this.obstacles = [];
    this.contextSelection = undefined;
    this.currentConfig = createInitialWorkspaceConfig();
    this.needsSimulation = true;
    this.error = undefined;
    this.scenarioSaved = false;
    this.scenarioSavedAt = undefined;
    this.workspaceMode = 'edit';
    this.measurementDistancePx = undefined;
    this.activeTool = 'select';
  }

  private snapObstacleEndpoints(obstacles: Obstacle[]): Obstacle[] {
    const threshold = 6;
    const dist = (ax: number, ay: number, bx: number, by: number) => Math.hypot(ax - bx, ay - by);
    const next = obstacles.map((o) => ({ ...o }));
    type SegmentKey = 'x' | 'y' | 'x2' | 'y2';
    const getNum = (obj: Obstacle, key: SegmentKey): number => {
      const value = obj[key];
      return typeof value === 'number' ? value : 0;
    };
    let changed = false;
    let iterations = 0;
    const maxIterations = 4;
    do {
      changed = false;
      iterations++;
      for (let i = 0; i < next.length; i++) {
        for (let j = i + 1; j < next.length; j++) {
          const pairs: Array<[SegmentKey, SegmentKey, SegmentKey, SegmentKey]> = [
            ['x', 'y', 'x', 'y'],
            ['x', 'y', 'x2', 'y2'],
            ['x2', 'y2', 'x', 'y'],
            ['x2', 'y2', 'x2', 'y2']
          ];
          for (const [axKey, ayKey, bxKey, byKey] of pairs) {
            const axv = getNum(next[i], axKey);
            const ayv = getNum(next[i], ayKey);
            const bxv = getNum(next[j], bxKey);
            const byv = getNum(next[j], byKey);
            const d = dist(axv, ayv, bxv, byv);
            if (d <= threshold) {
              const mx = (axv + bxv) / 2;
              const my = (ayv + byv) / 2;
              next[i] = { ...next[i], [axKey]: mx, [ayKey]: my };
              next[j] = { ...next[j], [bxKey]: mx, [byKey]: my };
              changed = true;
            }
          }
        }
      }
    } while (changed && iterations < maxIterations);
    return next;
  }

  private markSimulationNeeded(): void {
    this.points = [];
    this.needsSimulation = true;
    this.error = undefined;
    this.workspaceMode = 'edit';
    this.scenarioSaved = false;
  }

  private applyScenarioDraft(draft: { aps?: AccessPoint[]; obstacles?: Obstacle[] }): void {
    if (draft.aps) {
      this.accessPoints = draft.aps;
      this.currentConfig = { ...this.currentConfig, aps: draft.aps };
    }
    if (draft.obstacles) {
      this.obstacles = draft.obstacles;
      this.currentConfig = { ...this.currentConfig, obstacles: draft.obstacles };
    }
    this.syncContextSelection();
    this.markSimulationNeeded();
  }

  private applyScenarioSnapshot(config: SyntheticGridConfig): void {
    this.currentConfig = config;
    this.accessPoints = config.aps;
    this.obstacles = config.obstacles ?? [];
    this.observationHeight = config.observationHeight ?? this.observationHeight;
    this.syncContextSelection();
  }

  private syncContextSelection(): void {
    if (!this.contextSelection) {
      return;
    }
    if (this.contextSelection.kind === 'antenna' && !this.accessPoints[this.contextSelection.index]) {
      this.contextSelection = undefined;
      return;
    }
    if (this.contextSelection.kind === 'obstacle' && !this.obstacles[this.contextSelection.index]) {
      this.contextSelection = undefined;
    }
  }
}
